import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    isWidget,
    getBorderColourClassName,
    Sizes,
    Colours,
    CardBanner,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
} from '@ratehub/base-ui';

import PRODUCT_TYPES from '../definitions/ProductTypes';
import FOCUS_VARIANTS from '../definitions/FocusVariants';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import isCIBCCreditCard from '../functions/isCIBCCreditCard';
import getCardBannerProps from '../functions/getCardBannerProps';
import extractRhAnalyticsFromCreditCard from '../functions/extractRhAnalyticsFromCreditCard';
import CreditCardCTA from './CreditCardCTA';
import CardLayoutFooter from './CardLayoutFooter';
import CardLayoutTitleArea from './CardLayoutTitleArea';
import CardLayoutHighlights from './CardLayoutHighlights';
import ApprovalLikelihoodPill from './ApprovalLikelihoodPill';


function CreditCardLayout({
    cardInfo,
    productType,

    onClick,
    hasEligibilityCTA = false,
    borderColour,

    hasCategoryPill = false,
    hasBestUsedForPill = false,
    isCardFinder = false,
    isShowingApprovalLikelihood = false,

    focusVariant,
    isEqualizeHeight = false,
}) {
    const intl = useIntl();

    const {
        giftCardOffer,
        hasReferralOffer,
        cardImageURL,
        name,
        cardTypes,
        id: cardId,
        ratehubReview,
        isSponsored,
        isMonetized,
        displayType,
        bestUsedFor,
        approvalLikelihood,
    } = cardInfo;

    const isAWidget = isWidget();

    const colourOfBorder = borderColour || Colours.STONE;

    const isCIBC = isCIBCCreditCard(cardInfo);

    const rightColumnWidth = getCardWidth({ isWide: isCardFinder });

    const cardBannerProps = getCardBannerProps({ giftCardOffer, hasReferralOffer }, intl);


    return (
        <Container
            className={classNames('rh-box-sizing-border-box rh-bg-coconut rh-border-style-solid rh-border-width-1px',
                getBorderColourClassName(colourOfBorder),
                ANALYTICS_ATTRIBUTE_NAME
            )}
            ref={self => self && (self[ANALYTICS_KEY] = extractRhAnalyticsFromCreditCard(cardInfo))}
            borderRadius={BORDER_RADIUS}
            rightColumnWidth={rightColumnWidth}
            data-test-name={'credit-card'}
            data-test-product-type={productType}
        >
            <Choose>
                <When condition={cardBannerProps}>
                    <div className="rh-box-sizing-border-box">
                        <CardBanner
                            text={cardBannerProps.message}
                            variant={cardBannerProps.variant}
                        />
                    </div>
                </When>
                <Otherwise>
                    {/* there is no banner, but we might fill that space with a CardBanner placeholder */}
                    <If condition={isEqualizeHeight}>
                        <span className="rh-display-inline-block rh-icon-xs rh-my-0_25">
                            {' '}
                        </span>
                    </If>
                </Otherwise>
            </Choose>

            <div className="inner-body rh-display-flex rh-flex-wrap rh-px-0_5 rh-mb-1 rh-gap-0_5 rh-box-sizing-border-box">
                <div
                    className={classNames('row1 rh-display-flex rh-flex-wrap rh-box-sizing-border-box', {
                        'rh-mt-1': !isEqualizeHeight,
                        'rh-mt-0_25': isEqualizeHeight && giftCardOffer,
                    })}
                >
                    <div className="title-area rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutTitleArea
                            name={name}
                            ratehubReview={ratehubReview}
                            displayType={hasCategoryPill ? displayType : undefined}
                            bestUsedFor={hasBestUsedForPill ? bestUsedFor : undefined}
                            isSponsored={isSponsored && !isCardFinder}
                            isMonetized={isMonetized}

                            onClick={onClick}
                            isAWidget={isAWidget}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="card-image-wrapper rh-mx-0_25 rh-box-sizing-border-box">
                        <button
                            className="card-button rh-display-block rh-p-0 rh-m-auto rh-border-width-0"
                            data-name="card-image"
                            onClick={onClick}
                            type="button"
                        >
                            <img
                                src={cardImageURL}
                                alt={name}
                                width={rightColumnWidth}
                                height={getCardHeight({ isWide: isCardFinder })}
                            />
                        </button>
                    </div>
                </div>

                <div className="row2 rh-display-flex rh-flex-wrap-reverse rh-box-sizing-border-box">
                    <div className="body rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutHighlights
                            cardInfo={cardInfo}
                            productType={productType}
                            focusVariant={focusVariant}
                            isCIBC={isCIBC}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="cta-wrapper rh-mx-0_25 rh-mb-1_5 rh-box-sizing-border-box">
                        <If condition={isShowingApprovalLikelihood}>
                            <ApprovalLikelihoodPill
                                className="approval-likelihood-pill rh-mx-auto rh-mb-0_5 rh-box-sizing-border-box"
                                approvalLikelihood={approvalLikelihood}
                            />
                        </If>

                        <CreditCardCTA
                            cardTypes={cardTypes}
                            giftCardOffer={giftCardOffer}
                            hasReferralOffer={hasReferralOffer}
                            cardId={cardId}

                            buttonSize="small"
                            hasEligibilityCTA={hasEligibilityCTA}
                            onClick={onClick}
                        />
                    </div>
                </div>
            </div>

            <div
                className="rh-flex-grow-1 rh-flex-shrink-1 rh-width-100p rh-box-sizing-border-box"
                data-test-name="card-footer"
            >
                <CardLayoutFooter
                    cardInfo={cardInfo}
                    borderRadius={BORDER_RADIUS}
                    isAWidget={isAWidget}
                    isCIBC={isCIBC}
                />
            </div>
        </Container>
    );
}

CreditCardLayout.propTypes = {
    cardInfo: PropTypes.shape(CreditCardSynopsisShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)).isRequired,

    onClick: PropTypes.func,
    borderColour: PropTypes.oneOf(Object.values(Colours)),

    hasEligibilityCTA: PropTypes.bool,
    hasCategoryPill: PropTypes.bool,
    hasBestUsedForPill: PropTypes.bool,
    isCardFinder: PropTypes.bool,
    isShowingApprovalLikelihood: PropTypes.bool,

    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
    isEqualizeHeight: PropTypes.bool,
};


// we need a wider second column to accommodate approval likelihood message (CardFinder only) and longer FR CTA
// increasing this width means long card names are more likely to wrap (noting that some card names will always wrap)
function getCardWidth({ isWide = false } = {}) {
    return isWide ? '192px' : '182px';
}

function getCardHeight({ isWide = false } = {}) {
    return parseInt(getCardWidth({ isWide })) * 0.625 + 'px';
}

const BORDER_RADIUS = '4px';


const Container = styled.div`
    ${({ borderRadius }) => `border-radius: ${borderRadius}`};

    box-shadow: 1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 1px 2px 1px ${Colours.STONE_LIGHT},
        -1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 -1px 2px 1px ${Colours.STONE_LIGHT};

    > .inner-body {
        > .row1,
        > .row2 {
            flex: 1 1 100%;
            gap: 0 ${Sizes.SPACING.QUARTER};
        }

        > .row1 > .title-area,
        > .row2 > .body {
            flex: 30 1 400px; // greedy title-area and body widths
        }

        > .row1 > .card-image-wrapper,
        > .row2 > .cta-wrapper {
            flex: 1 0 ${({ rightColumnWidth }) => `${rightColumnWidth}`};
        }

        > .row2 {
            > .cta-wrapper {
                > .approval-likelihood-pill {
                    max-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                }
            }
        }
    }
`;


export default observer(CreditCardLayout);
